/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import CatererPdfViewer from './caterer-pdf-viewer';
import closeIcon from '../../../images/icons/Icon2.png';
import { APIEndpoint } from '../../../utils/axios';
import '../../../assets/styles/modal.scss';
import '../../../assets/styles/caterer-invoicing.scss';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaQuestionCircle,
  FaFileInvoiceDollar,
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaReceipt,
  FaHistory
} from 'react-icons/fa';
import moment from 'moment';
import { IS_DEMO_MODE, formatDemoCurrency } from '../../../assets/demo/demoInvoiceData';

const TYPE_BADGE_CLASS = {
  Service: 'caterer-badge--warning',
  Uplifts: 'caterer-badge--info',
  'Bulk Deliveries': 'caterer-badge--info'
};

const STATUS_BADGE = {
  Accepted: { className: 'caterer-badge--success', label: '✓ Accepted' },
  Rejected: { className: 'caterer-badge--error', label: '✕ Rejected' },
  Clarification: { className: 'caterer-badge--warning', label: '? Clarification Needed' },
  'Under Review': { className: 'caterer-badge--info', label: '⊙ Under Review' },
  'Duplicate Suspected': { className: 'caterer-badge--warning', label: '⚠ Duplicate Suspected' }
};

export const CatererInvoiceReviewModal = ({ invoice: initialInvoice, onClose, onActionUpdate }) => {
  const [invoice, setInvoice] = useState(initialInvoice);
  const [activeTab, setActiveTab] = useState('reconciliation'); // 'reconciliation' | 'financials' | 'audit'
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [clarificationNote, setClarificationNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionPrompt, setActionPrompt] = useState(null); // 'accept' | 'clarify' | 'reject' | null
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const isDemoInvoice = Boolean(initialInvoice?.id && String(initialInvoice.id).startsWith('DEMO-'));
  const skipNetwork = IS_DEMO_MODE || isDemoInvoice;

  useEffect(() => {
    // While IS_DEMO_MODE is on (or for a DEMO-* invoice specifically) every
    // screen here is self-contained (see demoInvoiceData.js) — never hit the
    // real backend, it won't recognize demo ids and there's nothing to sync.
    if (!skipNetwork && initialInvoice && initialInvoice.id) {
      APIEndpoint.get(`caterer-invoices/${initialInvoice.id}`)
        .then(res => {
          if (res && res.id) {
            setInvoice(prev => ({ ...prev, ...res }));
          }
        })
        .catch(err => {
          console.warn('Could not fetch latest invoice details from API:', err.message);
        });
    }

    if (initialInvoice?.selectedPdf) {
      setPdfBlobUrl(initialInvoice.selectedPdf);
    } else if (!skipNetwork && initialInvoice?.id) {
      const fullUrl = APIEndpoint.getFullPath(`caterer-invoices/${initialInvoice.id}/pdf`);
      fetch(fullUrl, {
        headers: {
          'user_id': localStorage.getItem('user_id') || '',
          'X-Bearer-Token': localStorage.getItem('bearer_token') || '',
          'X-CSRF-Token': localStorage.getItem('token') || ''
        }
      })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(objectUrl);
        })
        .catch(err => {
          console.warn('Could not load PDF blob from server:', err.message);
        });
    }
  }, [initialInvoice?.id, initialInvoice?.selectedPdf]);

  if (!invoice) return null;

  const decrementPageNo = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };

  const incrementPageNo = () => {
    if (pageNumber < numPages) setPageNumber(pageNumber + 1);
  };

  const handleConfirmAction = async (action) => {
    if (!skipNetwork) {
      try {
        await APIEndpoint.put(`caterer-invoices/${invoice.id}/status`, {
          status: action,
          note: action === 'Clarification' ? clarificationNote : (action === 'Rejected' ? rejectReason : '')
        });
      } catch (err) {
        console.warn('API update invoice status failed:', err.message);
      }
    }
    if (onActionUpdate) {
      onActionUpdate(invoice.id, action, {
        note: action === 'Clarification' ? clarificationNote : (action === 'Rejected' ? rejectReason : '')
      });
    }
    setActionPrompt(null);
    onClose();
  };

  // DEMO ONLY: hardcoded to the demo's currency (see demoInvoiceData.js) —
  // the real app should format via the tenant's own culture/currency
  // (getCultureFromLs()) instead of a fixed code.
  const formatCurrency = (val) => formatDemoCurrency(val);

  const getTypeBadgeClass = (type) => TYPE_BADGE_CLASS[type] || 'caterer-badge--neutral';

  const getStatusBadge = (status) => {
    const badge = STATUS_BADGE[status] || { className: 'caterer-badge--neutral', label: status };
    return <span className={`caterer-badge caterer-badge--pill ${badge.className}`}>{badge.label}</span>;
  };

  const hasVariance = invoice.variance > 0;

  return (
    <div className='overlay' role='dialog'>
      <div className='dialog-caterer sm-scrollbar'>
        <div className='dialog__content'>
          {/* MODAL HEADER */}
          <div className='dialog-header flexRow alignItemsCenter'>
            <div className='flexRow alignItemsCenter gap10'>
              <FaFileInvoiceDollar size={24} />
              <div>
                <div className='caterer-header__title-row'>
                  <h2 className='dialog__title'>Invoice: {invoice.invoiceId}</h2>
                  <span className={`caterer-badge caterer-badge--pill ${getTypeBadgeClass(invoice.invoiceType)}`}>
                    {invoice.invoiceType}
                  </span>
                  {getStatusBadge(invoice.status)}
                </div>
                <div className='caterer-header__meta'>
                  {invoice.catererName} | {invoice.locationCode || invoice.sector}
                </div>
              </div>
            </div>
            <button className='close-btn-ht' onClick={onClose}>
              <img alt='Close' src={closeIcon} />
            </button>
          </div>

          {/* MODAL BODY (SPLIT-SCREEN: RECONCILIATION / PDF VIEWER) */}
          <div className='dialog__body_container'>
            <div className='caterer-split'>

              {/* LEFT PANEL: RECONCILIATION & DETAILS */}
              <div className='caterer-split__form caterer-split__form--wide flexCol sm-scrollbar'>

                {/* INVOICE QUICK STATS */}
                <div className='caterer-stats'>
                  <div className='caterer-stat-card'>
                    <div className='caterer-stat-card__label'>Billed Subtotal</div>
                    <div className='caterer-stat-card__value'>{formatCurrency(invoice.subTotal)}</div>
                  </div>
                  <div className='caterer-stat-card'>
                    <div className='caterer-stat-card__label'>Tax Amount</div>
                    <div className='caterer-stat-card__value'>{formatCurrency(invoice.taxAmount)}</div>
                  </div>
                  <div className='caterer-stat-card'>
                    <div className='caterer-stat-card__label'>Total Payable</div>
                    <div className='caterer-stat-card__value'>{formatCurrency(invoice.total)}</div>
                  </div>
                  <div className={`caterer-stat-card ${hasVariance ? 'caterer-stat-card--warning' : 'caterer-stat-card--success'}`}>
                    <div className='caterer-stat-card__label'>Variance</div>
                    <div className='caterer-stat-card__value'>
                      {hasVariance ? `+${formatCurrency(invoice.variance)}` : `${formatCurrency(0)} (Match)`}
                    </div>
                  </div>
                </div>

                {/* TAB NAVIGATION */}
                <div className='caterer-tabs'>
                  <button
                    className={`caterer-tabs__btn ${activeTab === 'reconciliation' ? 'caterer-tabs__btn--active' : ''}`}
                    onClick={() => setActiveTab('reconciliation')}
                  >
                    <FaReceipt size={14} /> Line-Item Reconciliation
                  </button>
                  <button
                    className={`caterer-tabs__btn ${activeTab === 'financials' ? 'caterer-tabs__btn--active' : ''}`}
                    onClick={() => setActiveTab('financials')}
                  >
                    <FaFileInvoiceDollar size={14} /> Tax & Commercials
                  </button>
                  {/* Audit Trail & Metadata tab hidden for now — re-enable by restoring this button and the 'audit' tab-content block below. */}
                </div>

                {/* TAB 1: RECONCILIATION TABLE */}
                {activeTab === 'reconciliation' && (
                  <>
                    {/* Fixed header for this tab — stays put while the table beneath it scrolls. */}
                    <div className='caterer-recon-summary'>
                      <div className='flexRow justifyContentSpaceBetween alignItemsCenter'>
                        <div style={{ fontWeight: 600 }}>
                          Operational 3-Way Match ({invoice.lineItems?.length || 0} Line Items)
                        </div>
                        <div style={{ fontSize: '0.78rem', opacity: 0.75 }}>
                          Matched against: <strong>
                            {invoice.invoiceType === 'Service' ? 'dfpItems (Flight Schedule)' : (invoice.invoiceType === 'Uplifts' ? 'uplifts.items (Catering Officer Sign-off)' : 'purchaseOrderGrn (Dock Receipts)')}
                          </strong>
                        </div>
                      </div>

                      {hasVariance && (
                        <div className='caterer-alert caterer-alert--warning'>
                          <FaExclamationTriangle size={18} />
                          <span>
                            <strong>Discrepancy Detected:</strong> One or more billed quantities exceed signed operational records. Review flagged rows below before approving.
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='caterer-table-wrap'>
                    <table id='caterer-recon-table' className='caterer-table'>
                      <colgroup>
                        <col /><col /><col /><col /><col /><col /><col />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Item Description</th>
                          <th className='text-center'>Billed Qty</th>
                          <th className='text-center'>Verified Qty</th>
                          <th className='text-right'>Unit Rate</th>
                          <th className='text-right'>Billed Amount</th>
                          <th className='text-right'>Variance</th>
                          <th className='text-center'>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.lineItems && invoice.lineItems.map((item, idx) => {
                          const itemHasVar = item.variance > 0;
                          const isUnresolved = item.status === 'Unresolved Item';
                          return (
                            <React.Fragment key={idx}>
                              <tr className={itemHasVar || isUnresolved ? 'caterer-table__row--variance' : ''}>
                                <td>
                                  <div>{item.description}</div>
                                  <div className='caterer-table__item-code'>{item.itemCode}</div>
                                </td>
                                <td className='text-center'>{item.billedQty}</td>
                                <td className='text-center'>{item.dbQty}</td>
                                <td className='text-right'>{formatCurrency(item.unitRate)}</td>
                                <td className='text-right'>{formatCurrency(item.billedAmount)}</td>
                                <td className='text-right'>
                                  {itemHasVar ? `+${formatCurrency(item.variance)}` : formatCurrency(0)}
                                </td>
                                <td className='text-center'>
                                  {isUnresolved ? (
                                    <span className='caterer-badge caterer-badge--pill caterer-badge--error'>Unresolved</span>
                                  ) : itemHasVar ? (
                                    <span className='caterer-badge caterer-badge--pill caterer-badge--warning'>Variance</span>
                                  ) : (
                                    <span className='caterer-badge caterer-badge--pill caterer-badge--success'>Match</span>
                                  )}
                                </td>
                              </tr>
                              {item.varianceReason && (
                                itemHasVar || isUnresolved ? (
                                  <tr className='caterer-table__reason-row'>
                                    <td colSpan={7}>
                                      ⚠️ <strong>Variance Reason:</strong> {item.varianceReason}
                                    </td>
                                  </tr>
                                ) : (
                                  <tr className='caterer-table__note-row'>
                                    <td colSpan={7}>
                                      ✓ <strong>Verification Note:</strong> {item.varianceReason}
                                    </td>
                                  </tr>
                                )
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}

                {/* TAB 2: FINANCIALS */}
                {activeTab === 'financials' && (
                  <div className='caterer-tab-content'>
                    <div className='caterer-financials-card'>
                      <h4 className='caterer-financials-card__title'>Commercial & Tax Breakdown</h4>
                      <div className='caterer-financials-card__grid'>
                        <div className='caterer-financials-card__key'>Billed Subtotal (Pre-Tax):</div>
                        <div className='caterer-financials-card__value'>{formatCurrency(invoice.subTotal)}</div>

                        {/* DEMO ONLY: this whole demo runs on TVA (Morocco has
                            no GST) — CGST/SGST/IGST is an India-specific split
                            that isn't used anywhere in the demo, including the
                            6 invoices whose vendors are real Indian entities.
                            Needs a real per-country tax-regime implementation
                            post-demo (see verifyTaxLogic on the backend). */}
                        <div className='caterer-financials-card__key'>{invoice.taxLabel || 'Tax'}:</div>
                        <div className='caterer-financials-card__value'>{formatCurrency(invoice.taxAmount)}</div>

                        <div className='caterer-financials-card__total-row'>Net Amount Payable:</div>
                        <div className='caterer-financials-card__total-row caterer-financials-card__value'>{formatCurrency(invoice.total)}</div>
                      </div>
                    </div>

                    <div className='caterer-financials-card'>
                      <h4 className='caterer-financials-card__title'>Entity & Statutory Identifiers</h4>
                      <div className='caterer-financials-card__grid'>
                        <div className='caterer-financials-card__key'>Caterer Legal Name:</div>
                        <div>{invoice.catererName}</div>

                        {invoice.gstIn ? (
                          <>
                            <div className='caterer-financials-card__key'>Caterer GSTIN:</div>
                            <div style={{ fontFamily: 'monospace' }}>{invoice.gstIn}</div>
                          </>
                        ) : invoice.taxRegistrationLabel ? (
                          <>
                            <div className='caterer-financials-card__key'>{invoice.taxRegistrationLabel}:</div>
                            <div style={{ fontFamily: 'monospace' }}>{invoice.taxRegistrationValue}</div>
                          </>
                        ) : null}

                        <div className='caterer-financials-card__key'>Place of Supply / Station:</div>
                        <div>{invoice.locationCode || invoice.sector || '—'}</div>

                        <div className='caterer-financials-card__key'>Payment Terms:</div>
                        <div>{invoice.paymentTerms || 'Net30'}</div>

                        {invoice.poNumber && (
                          <>
                            <div className='caterer-financials-card__key'>Purchase Order (PO):</div>
                            <div style={{ fontWeight: 600 }}>{invoice.poNumber}</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 3: AUDIT TRAIL */}
                {activeTab === 'audit' && (
                  <div className='caterer-tab-content'>
                    <div className='caterer-financials-card'>
                      <h4 className='caterer-financials-card__title'>Ingestion & Reconciliation Log</h4>
                      <div className='caterer-audit-log'>
                        <div className='caterer-audit-log__row'>
                          <div className='caterer-audit-log__dot caterer-audit-log__dot--success'>●</div>
                          <div>
                            <div className='caterer-audit-log__title'>Digitized & Parsed by Forthcode AI Service</div>
                            <div className='caterer-audit-log__meta'>
                              Digital text layer parsed • Schema verified{invoice.extractedData?.extractionMethod === 'heuristic' ? ' • Low-confidence heuristic fallback used' : ''}
                            </div>
                          </div>
                        </div>
                        <div className='caterer-audit-log__row'>
                          <div className='caterer-audit-log__dot caterer-audit-log__dot--info'>●</div>
                          <div>
                            <div className='caterer-audit-log__title'>Cross-Verified Against Tenant Operational Database</div>
                            <div className='caterer-audit-log__meta'>
                              Matched against {invoice.invoiceType === 'Service' ? 'dfpItems turnarounds' : (invoice.invoiceType === 'Uplifts' ? 'uplifts sign-offs' : 'purchaseOrderGrn deliveries')}
                              {invoice.vendorMatchStatus ? ` • Supplier match: ${invoice.vendorMatchStatus}` : ''}
                            </div>
                          </div>
                        </div>
                        <div className='caterer-audit-log__row'>
                          <div className={`caterer-audit-log__dot ${hasVariance ? 'caterer-audit-log__dot--warning' : 'caterer-audit-log__dot--success'}`}>●</div>
                          <div>
                            <div className='caterer-audit-log__title'>Reconciliation Result: {hasVariance ? 'Variance Detected' : '100% Reconciled'}</div>
                            <div className='caterer-audit-log__meta'>Status set to: {invoice.status}</div>
                          </div>
                        </div>
                        {invoice.taxLogicStatus && invoice.taxLogicStatus.expected !== 'unknown' && (
                          <div className='caterer-audit-log__row'>
                            <div className={`caterer-audit-log__dot ${invoice.taxLogicStatus.valid ? 'caterer-audit-log__dot--success' : 'caterer-audit-log__dot--warning'}`}>●</div>
                            <div>
                              <div className='caterer-audit-log__title'>
                                GST Split Check: {invoice.taxLogicStatus.valid ? 'Consistent' : 'Mismatch Detected'}
                              </div>
                              <div className='caterer-audit-log__meta'>
                                Expected {invoice.taxLogicStatus.expected} based on supplier/warehouse state, invoice shows {invoice.taxLogicStatus.actual}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* MANAGER ACTION BAR */}
                <div className='caterer-action-bar'>
                  <div style={{ fontSize: '0.8rem', opacity: 0.75 }}>
                    Signed-in as: <strong>Catering Operations Manager</strong>
                  </div>

                  <div className='flexRow gap10'>
                    <button className='caterer-action-btn caterer-action-btn--clarify' onClick={() => setActionPrompt('clarify')}>
                      <FaQuestionCircle /> Request Clarification
                    </button>
                    <button className='caterer-action-btn caterer-action-btn--reject' onClick={() => setActionPrompt('reject')}>
                      <FaTimesCircle /> Reject Invoice
                    </button>
                    <button className='caterer-action-btn caterer-action-btn--accept' onClick={() => setActionPrompt('accept')}>
                      <FaCheckCircle /> Accept Invoice
                    </button>
                  </div>
                </div>

              </div>

              {/* RIGHT PANEL: PDF VIEWER */}
              <div className='caterer-split__preview caterer-split__preview--narrow'>
                <div className='caterer-preview__header'>
                  <span>Original Invoice Document (PDF)</span>
                  <span className='flexRow alignItemsCenter gap10 pointer'>
                    <FaChevronCircleLeft size={16} onClick={decrementPageNo} />
                    Page {pageNumber} of {numPages}
                    <FaChevronCircleRight size={16} onClick={incrementPageNo} />
                  </span>
                </div>

                <div className='caterer-preview__body sm-scrollbar'>
                  {/* If invoice has a direct PDF file, blob URL, or server PDF, render viewer; otherwise fallback to a paper-style summary */}
                  {(pdfBlobUrl || invoice.selectedPdf || invoice.pdfFilePath) ? (
                    <CatererPdfViewer
                      fileHolder={pdfBlobUrl || invoice.selectedPdf}
                      numPages={numPages}
                      pageNumber={pageNumber}
                      setNumPages={setNumPages}
                      setPageNumber={setPageNumber}
                    />
                  ) : (
                    <div className='caterer-invoice-mock'>
                      <div className='caterer-invoice-mock__header'>
                        <div>
                          <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem' }}>{invoice.catererName}</h3>
                          <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{invoice.address}</div>
                          <div style={{ fontSize: '0.75rem', marginTop: '2px' }}>GSTIN: <strong>{invoice.gstIn}</strong></div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0052cc' }}>TAX INVOICE</div>
                          <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>Invoice: <strong>{invoice.invoiceId}</strong></div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>Date: {invoice.date ? moment(invoice.date).format('DD-MMM-YYYY') : '—'}</div>
                        </div>
                      </div>

                      <div className='caterer-invoice-mock__meta'>
                        <div>Period: <strong>{invoice.period || '—'}</strong></div>
                        <div>Sector/Station: <strong>{invoice.locationCode || invoice.sector || '—'}</strong></div>
                        <div>Flight: <strong>{invoice.flightNumber || '—'}</strong></div>
                        <div>PO: <strong>{invoice.poNumber || '—'}</strong></div>
                      </div>

                      <table>
                        <colgroup>
                          <col style={{ width: '44%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '22%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>Item Description</th>
                            <th style={{ textAlign: 'center' }}>Qty</th>
                            <th style={{ textAlign: 'right' }}>Unit Rate</th>
                            <th style={{ textAlign: 'right' }}>Billed Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.lineItems && invoice.lineItems.map((it, i) => (
                            <tr key={i}>
                              <td>{it.description}</td>
                              <td style={{ textAlign: 'center' }}>{it.billedQty}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(it.unitRate)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(it.billedAmount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div className='caterer-invoice-mock__totals'>
                        <div className='caterer-invoice-mock__totals-box'>
                          <div className='caterer-invoice-mock__totals-row'>
                            <span>Subtotal:</span>
                            <span>{formatCurrency(invoice.subTotal)}</span>
                          </div>
                          <div className='caterer-invoice-mock__totals-row'>
                            <span>{invoice.taxLabel || 'Tax (GST)'}:</span>
                            <span>{formatCurrency(invoice.taxAmount)}</span>
                          </div>
                          <div className='caterer-invoice-mock__totals-row caterer-invoice-mock__totals-row--grand'>
                            <span>Total Payable:</span>
                            <span style={{ color: '#0052cc' }}>{formatCurrency(invoice.total)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CONFIRMATION ACTION MODAL PROMPT */}
        {actionPrompt && (
          <div className='caterer-prompt-overlay'>
            <div className={`caterer-prompt-card caterer-prompt-card--${actionPrompt}`}>

              {actionPrompt === 'accept' && (
                <>
                  <div className='caterer-prompt-card__header'>
                    <FaCheckCircle size={22} />
                    <h3 className='caterer-prompt-card__title' style={{ margin: 0 }}>Accept Invoice</h3>
                  </div>
                  <p className='caterer-prompt-card__body'>
                    Are you sure you want to approve invoice <strong>{invoice.invoiceId}</strong> for <strong>{formatCurrency(invoice.total)}</strong>? This will confirm operational sign-off and unlock it for finance payment.
                  </p>
                  <div className='caterer-prompt-card__actions'>
                    <button className='cancel-button' onClick={() => setActionPrompt(null)}>Cancel</button>
                    <button className='done-button' onClick={() => handleConfirmAction('Accepted')}>Confirm Approval</button>
                  </div>
                </>
              )}

              {actionPrompt === 'clarify' && (
                <>
                  <div className='caterer-prompt-card__header'>
                    <FaQuestionCircle size={22} />
                    <h3 className='caterer-prompt-card__title' style={{ margin: 0 }}>Request Clarification</h3>
                  </div>
                  <p className='caterer-prompt-card__body'>
                    Specify the variance query or details you require from {invoice.catererName}:
                  </p>
                  <textarea
                    rows={3}
                    className='edit-input caterer-field--full'
                    value={clarificationNote}
                    onChange={(e) => setClarificationNote(e.target.value)}
                    placeholder='e.g. Please clarify discrepancy between billed and signed uplift quantities.'
                  />
                  <div className='caterer-prompt-card__actions'>
                    <button className='cancel-button' onClick={() => setActionPrompt(null)}>Cancel</button>
                    <button className='done-button' onClick={() => handleConfirmAction('Clarification')}>Send Query</button>
                  </div>
                </>
              )}

              {actionPrompt === 'reject' && (
                <>
                  <div className='caterer-prompt-card__header'>
                    <FaTimesCircle size={22} />
                    <h3 className='caterer-prompt-card__title' style={{ margin: 0 }}>Reject Invoice</h3>
                  </div>
                  <p className='caterer-prompt-card__body'>
                    Please enter the rejection reason for this invoice:
                  </p>
                  <textarea
                    rows={3}
                    className='edit-input caterer-field--full'
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder='e.g. Duplicate billing detected / Incorrect station GSTIN applied.'
                  />
                  <div className='caterer-prompt-card__actions'>
                    <button className='cancel-button' onClick={() => setActionPrompt(null)}>Cancel</button>
                    <button className='done-button' onClick={() => handleConfirmAction('Rejected')}>Confirm Rejection</button>
                  </div>
                </>
              )}

            </div>
          </div>
        )}

      </div>
    </div>
  );
};
